import "./App.css";
import "./normalize.css";
import React, { useReducer, useEffect } from "react";
import { HashRouter } from "react-router-dom";
import Main from "./components/main/Main.component";
import StateContext from "./context/StateContext";
import { loadStripe } from "@stripe/stripe-js/pure";
import { Elements } from "@stripe/react-stripe-js";
import styled from "styled-components";
import FixelLoaderModal from "./components/commons/Loader/FixelLoaderModal";
import { ModalProvider, BaseModalBackground } from "styled-react-modal";
import Firebase from "./services/firebase";
import { GlobalStyle } from "./global";
import plansList from "./components/plans/plansList";
import { prepPlansMap, prepWithExtras } from "./services/dataService";

// init stripe payment context
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
const FadingBackground = styled(BaseModalBackground)`
	opacity: ${(props) => props.opacity};
	transition: all 1s ease-in-out;
`;

/**
 * List of actions
 * @type {{SET_HAVE_DATA: string, SET_USER: string, SET_HAVE_SEGMENTS: string, SET_LOADING: string}}
 */
const ACTIONS = {
	SET_HAVE_DATA: "set-have-data",
	SET_HAVE_SEGMENTS: "set-have-segments",
	SET_USER: "set-user",
	SET_LOADING: "set-loading",
	SET_SPINNER: "set-spinner",
	SET_SEG_MAP: "set-seg-map",
	SET_HITS_MAP: "set-hits-map",
	SET_AUD_MAP: "set-aud-map",
	SET_FIREBASE_INITIALIZED: "set-firebase-initialized",
	SET_CUSTOMER: "set-customer",
	SET_PLANS: "set-plans",
	SET_SEGMENTS: "set-segments",
	SET_CODE: "set-selected-code",
	SET_WEBSITE: "set-websiteURL",
	SET_STATE: "set-state",
	RESET_STATE: "reset-state",
	RELOAD_DATA: "reload-data"
};

/**
 * Main reduced function for dealing with state
 * @param state
 * @param action
 * @returns {(*&{haveSegments})|(*&{loading})|(*&{haveData})|(*&{user})|*}
 */
const stateReducerFunc = (state, action) => {
	switch (action.type) {
		case ACTIONS.SET_USER:
			return { ...state, user: action.payload };
		case ACTIONS.SET_HAVE_SEGMENTS:
			return { ...state, haveSegments: action.payload };
		case ACTIONS.SET_HAVE_DATA:
			return { ...state, haveData: action.payload };
		case ACTIONS.SET_LOADING:
			return { ...state, loading: action.payload };
		case ACTIONS.SET_SPINNER:
			return { ...state, showSpinner: action.payload };
		case ACTIONS.SET_PLANS_MAP:
			return { ...state, plansMap: action.payload };
		case ACTIONS.SET_SEG_MAP:
			return { ...state, segMap: action.payload };
		case ACTIONS.SET_HITS_MAP:
			return { ...state, hitsMap: action.payload };
		case ACTIONS.SET_AUD_MAP:
			return { ...state, audMap: action.payload };
		case ACTIONS.SET_PLANS:
			return { ...state, plans: action.payload, plansMap: prepPlansMap(action.payload) };
		case ACTIONS.SET_SEGMENTS: {
			const { segMap, segmentsArray } = prepWithExtras(action.payload);
			return { ...state, segments: segmentsArray, segMap };
		}
		case ACTIONS.SET_CODE:
			return { ...state, selectedSegmentCode: action.payload };
		case ACTIONS.SET_WEBSITE:
			return { ...state, websiteURL: action.payload };
		case ACTIONS.SET_FIREBASE_INITIALIZED:
			return { ...state, firebaseInitialized: action.payload };
		case ACTIONS.SET_CUSTOMER:
			return { ...state, customer: action.payload };
		case ACTIONS.SET_STATE:
			return { ...state, ...action.payload };
		case ACTIONS.RESET_STATE:
			return { ...resetState };
		case ACTIONS.RELOAD_DATA:
			return {...state, reloadData: !state.reloadData}

		default:
			return state;
	}
};

const initialState = {
	haveData: false,
	haveSegments: false,
	user: Firebase.getUser(),
	loading: true,
	plansMap: null,
	segMap: null,
	hitsMap: null,
	audMap: null,
	websiteURL: "",
	selectedSegmentCode: "",
	showSpinner: false,
	firebaseInitialized: false,
	customer: null,
	plans: null,
	segments: null,
	firebase: Firebase,
	plansList,
	reloadData: false
};
const resetState = {
	haveData: false,
	haveSegments: false,
	user: null,
	loading: false,
	showSpinner: false,
	firebaseInitialized: true,
	customer: null,
	plans: null,
	segments: null,
	haveCustomer: false,
	havePlans: false,
	selectedSegmentCode: null,
	plansMap: null,
	segMap: null,
	hitsMap: null,
	audMap: null,
	websiteURL: "",
	plansList
};
function App() {
	const [state, dispatch] = useReducer(stateReducerFunc, initialState);

	useEffect(() => {
		let didCancel = false;
		if (!state.firebaseInitialized) {
			const initializeFB = async () => {
				try {
					const res = await Firebase.isInitialized();
					//firebase is initialized here, check if res has a user
					const payload = { firebaseInitialized: true };
					if (res) payload.user = res;
					if (!didCancel) {
						dispatch({
							type: ACTIONS.SET_STATE,
							payload
						});
					}
				} catch (error) {
					console.error("App.js ~ line 104 ~ initializeFB ~ error", error);
					if (!didCancel) {
						dispatch({
							type: ACTIONS.SET_FIREBASE_INITIALIZED,
							payload: false
						});
					}
				}
			};
			initializeFB();
			return () => (didCancel = true);
		}
	}, [state.user]);

	return (
		<div className="App">
			<Elements stripe={stripePromise}>
				<GlobalStyle />
				<ModalProvider backgroundComponent={FadingBackground}>
					<StateContext.Provider value={{ ...state, dispatch, ACTIONS: ACTIONS }}>
						<HashRouter>
							<FixelLoaderModal show={state.loading || state.showSpinner} />
							{state.firebaseInitialized ? <Main /> : null}
						</HashRouter>
					</StateContext.Provider>
				</ModalProvider>
			</Elements>
		</div>
	);
}

export default App;
